import React, { useState, useEffect } from "react";
import GoToTop from "../GoToTop";
import { useParams } from "react-router-dom";
import { supabase } from "../supabase"; // make sure this is configured
import { formatINR } from "../utils/currency";
// import { GST } from "../API_KEYS";
import fuel from "./assets/images/fuel.webp";
import seat from "./assets/images/seat.webp";
import Loader from "./Loader";
import driver from "./assets/images/driver.png";

function MyBookingDetails() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const { data, error } = await supabase
          .from("Bookings")
          .select(
            `
          *,
          Vehicles (*), Drivers(*)   
        `,
          )
          .eq("id", id)
          .single();

        if (error) {
          console.error("Error fetching booking:", error);
        } else {
          setBooking(data);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBooking();
    }
  }, [id]);

  const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return "0 mins";

    const totalMinutes = Math.round(Number(minutes));

    if (isNaN(totalMinutes)) return "0 mins";

    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} mins`;
    if (hrs > 0) return `${hrs} hr`;
    return `${mins} mins`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";

    return new Date(timestamp).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatTimestampDate = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTimestampTime = (value) => {
    if (!value) return "N/A";

    const date = new Date(value);

    return date.toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateRideDays = (start, end) => {
    if (!start || !end) return 0;

    // Convert Firestore Timestamp to JS Date
    const startDate = start?.seconds
      ? new Date(start.seconds * 1000)
      : new Date(start);

    const endDate = end?.seconds ? new Date(end.seconds * 1000) : new Date(end);

    const diffInMs = endDate - startDate;

    if (diffInMs <= 0) return 0;

    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays;
  };

  const days = calculateRideDays(booking?.ride_start, booking?.ride_end);

  const dailyRate = Number(booking?.daily_rate || 0);
  const kmPrice = Number(booking?.km_price || 0);
  const meter = Number(booking?.meter_reading || 0);
  const advance = Number(booking?.preBookingFee || 0);
  const totalPrice = Number(booking?.total_price || 0);
  const GST = Number(booking?.gst || 0);

  let finalAmount = 0;

  if (booking?.booking_type === "Reserve") {
    const base = days * dailyRate + kmPrice * meter;
    const gstAmount = ((base + advance) * GST) / 100;

    finalAmount = base + gstAmount; // ✅ correct subtraction
  } else {
    finalAmount = totalPrice + (totalPrice * GST) / 100;
  }

  return (
    <>
      <GoToTop />
      <div className="container web-main" style={{ marginTop: "90px" }}>
        <h6 style={{ color: "grey" }}>
          Booking Details
          {/* <small style={{ textTransform: "lowercase" }}>
            {booking?.pickup}-to-{booking?.drop}
          </small> */}
        </h6>

        {loading && <Loader />}

        {!loading && booking && (
          <>
            <div className="uber-summary-card">
              <div className="uber-locations">
                <div className="location-row">
                  <div className="dot pickup-dot"></div>
                  <div className="location-text">
                    <span className="label">Pickup</span>
                    <span className="value">{booking?.pickup}</span>
                  </div>
                </div>

                <div className="short-vertical-line"></div>

                <div className="location-row">
                  <div className="square drop-square"></div>
                  <div className="location-text">
                    <span className="label">Drop</span>
                    <span className="value">{booking?.drop}</span>
                  </div>
                </div>
              </div>

              <div className="uber-meta">
                {booking?.isScheduled ? (
                  <span className="meta-badge">
                    Scheduled • {formatTimestampDate(booking?.pickupTime)},{" "}
                    {formatTimestampTime(booking?.pickupTime)}
                  </span>
                ) : (
                  <>
                    <span className="meta-badge">Instant pickup</span>{" "}
                    <span className="meta-badge bg-dark text-light">
                      Pick & Drop
                    </span>
                  </>
                )}

                {booking?.distance && booking?.duration && (
                  <span className="meta-time">
                    {booking?.distance} km • {formatDuration(booking?.duration)}
                  </span>
                )}
              </div>
            </div>

            {booking?.isScheduled ? (
              <div className="uber-date-card mt-2">
                <div className="uber-date-row">
                  <div>
                    <div className="date-label">Pickup</div>
                    <div className="date-value">
                      {formatTimestampDate(booking?.pickupTime)}
                    </div>
                    <div className="date-sub">
                      {formatTimestampTime(booking?.pickupTime)}
                    </div>
                  </div>
                </div>

                <div className="divider"></div>

                <div className="uber-date-row">
                  <div>
                    <div className="date-label">Return</div>
                    <div className="date-value">
                      {formatTimestampDate(booking?.pickupTime)}
                    </div>
                    <div className="days-badge">
                      ( {booking?.NoOfDays}{" "}
                      {booking?.NoOfDays > 1 ? "Days" : "Day"} )
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="d-flex justify-content-between align-items-center p-3 rounded-4 mb-2 border uber-ride-card mt-2">
              <div className="d-flex align-items-center gap-3">
                <img
                  src={booking?.Vehicles?.img_url}
                  className="img-fluid"
                  style={{ width: "85px", mixBlendMode: "darken" }}
                />

                <div>
                  <h6 className="mb-0">
                    <span style={{ textTransform: "Capitalize" }}>
                      {booking.veh_color}
                    </span>{" "}
                    {booking?.Vehicles?.name}{" "}
                  </h6>
                  <small
                    className="mb-0"
                    style={{ fontSize: "12px", marginTop: "-5px" }}
                  >
                    {booking?.veh_number}
                  </small>
                </div>
              </div>

              <div style={{ textAlign: "left" }}>
                <h6 className="mb-0" style={{ fontSize: "13px" }}>
                  <img src={fuel} className="icon-img" />
                  {booking?.Vehicles?.fuel}
                </h6>
                <h6
                  className="mb-0"
                  style={{ fontSize: "13px", marginTop: "5px" }}
                >
                  <img src={seat} className="icon-img" />
                  {booking?.Vehicles?.seat} seater
                </h6>
              </div>
            </div>

            <div className="d-flex align-items-center p-3 rounded-4 mb-2 border uber-ride-card mt-2">
              {/* Left Section */}
              <div className="d-flex gap-3 align-items-center">
                <img
                  src={booking?.Drivers?.profile_img || driver}
                  className="img-fluid"
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "20%",
                  }}
                  alt="driver"
                />

                <div>
                  <small
                    className="mb-0"
                    style={{ fontSize: "12px", color: "grey" }}
                  >
                    #DGDR-2026-0{booking?.Drivers?.id}
                  </small>
                  <h6 className="mb-1">{booking?.Drivers?.name}</h6>
                </div>
              </div>

              {/* Right Section */}
              {booking?.Drivers?.rating && (
                <div className="ms-auto text-end">
                  <small style={{ margin: 0 }}>
                    ⭐ <b>{booking?.Drivers?.rating}</b>
                    <span style={{ color: "grey" }}>/5</span>
                  </small>
                </div>
              )}
            </div>

            <div className="p-3 rounded-4 mb-2 border mt-2">
              <div
                className="d-flex align-items-center justify-content-between mb-2"
                style={{ fontSize: "13px" }}
              >
                {/* Left Section */}

                <p className="mb-0">Ride Started: </p>

                {/* Right Section */}

                <b style={{ color: "green" }}>
                  {formatTimestamp(booking.ride_start) || ""}
                </b>
              </div>

              <div
                className="d-flex align-items-center justify-content-between"
                style={{ fontSize: "13px" }}
              >
                {/* Left Section */}

                <p className="mb-0">Ride Ended: </p>

                {/* Right Section */}

                <b style={{ color: "red" }}>
                  {formatTimestamp(booking.ride_end) || ""}
                </b>
              </div>
            </div>

            <div className="p-3 rounded-4 mb-2 border mt-2">
              <div className="row">
                {booking?.booking_type == "Reserve" ? (
                  <>
                    <div className="col-8">
                      <small className="mb-0" style={{ fontSize: "12.5px" }}>
                        Daily Charge{" "}
                        <i className="mb-0">
                          ( {booking?.NoOfDays} day(s) x ₹{booking?.daily_rate}{" "}
                          )
                        </i>{" "}
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(
                          parseFloat(
                            booking?.NoOfDays * booking?.daily_rate,
                          ).toFixed(2),
                        )}
                      </b>
                    </div>
                    <div className="col-8">
                      <small style={{ fontSize: "12.5px" }}>
                        Total KM Rate{" "}
                        <i>
                          ( ₹{booking.km_price} x {booking.meter_reading} kms )
                        </i>
                        :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(
                          parseFloat(
                            booking.km_price * booking.meter_reading,
                          ).toFixed(2),
                        )}
                      </b>
                    </div>
                    <div className="col-8">
                      <small style={{ fontSize: "12.5px" }}>
                        GST ({GST}%) :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <span style={{ fontSize: "14px" }}>
                        {formatINR(
                          parseFloat(
                            ((booking?.NoOfDays * booking.daily_rate +
                              booking.km_price * booking.meter_reading +
                              booking.preBookingFee) *
                              GST) /
                              100,
                          ).toFixed(2),
                        )}
                      </span>
                    </div>
                    <hr className="mb-2 mt-2" />
                    <div className="col-6">
                      <small style={{ fontSize: "15px" }}>Grand Total :</small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-6">
                      <b style={{ fontSize: "17px", color: "green" }}>
                        {formatINR(finalAmount)}
                      </b>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-8">
                      <small style={{ fontSize: "14px" }}>
                        Booking Total :
                      </small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <b style={{ fontSize: "14px" }}>
                        {formatINR(booking.total_price)}
                      </b>
                    </div>
                    <div className="col-8">
                      <small style={{ fontSize: "12.5px" }}>GST (5%) :</small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-4">
                      <span style={{ fontSize: "14px" }}>
                        {formatINR((booking.total_price * GST) / 100)}
                      </span>
                    </div>
                    <hr className="mb-2 mt-2" />

                    <div className="col-6">
                      <small style={{ fontSize: "15px" }}>Grand Total :</small>
                    </div>
                    <div style={{ textAlign: "right" }} className="col-6">
                      <b style={{ fontSize: "17px", color: "green" }}>
                        {formatINR(
                          Number(booking.total_price) +
                            (booking.total_price * GST) / 100,
                        )}
                      </b>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {!loading && !booking && <p>No booking found.</p>}
      </div>
    </>
  );
}

export default MyBookingDetails;
